import { useState, useCallback } from "react";
import { getEvaluationState, startEvaluation } from "../api/evaluation-api";
import type { EvaluationState } from "../types/evaluation.type";

interface UseEvaluationOptions {
	rfxId: number;
	rfxSubmissionId?: number;
}

interface UseEvaluationResult {
	currentStep: number;
	error: string | null;
	evaluationState: EvaluationState | null;
	hasStarted: boolean;
	isLoading: boolean;
	fetchEvaluationState: () => Promise<void>;
	startEvaluation: () => Promise<EvaluationState | null>;
}

export const useEvaluation = ({
	rfxId,
	rfxSubmissionId,
}: UseEvaluationOptions): UseEvaluationResult => {
	const [evaluationState, setEvaluationState] =
		useState<EvaluationState | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const fetchEvaluationState = useCallback(async (): Promise<void> => {
		setIsLoading(true);
		setError(null);
		try {
			const state = await getEvaluationState(rfxId, rfxSubmissionId);
			setEvaluationState(state);
		} catch {
			setError("Failed to load evaluation state");
		} finally {
			setIsLoading(false);
		}
	}, [rfxId, rfxSubmissionId]);

	const handleStartEvaluation =
		useCallback(async (): Promise<EvaluationState | null> => {
			setIsLoading(true);
			setError(null);
			try {
				const state = await startEvaluation({ rfxId, rfxSubmissionId });
				setEvaluationState(state);
				return state;
			} catch {
				setError("Failed to start evaluation");
				return null;
			} finally {
				setIsLoading(false);
			}
		}, [rfxId, rfxSubmissionId]);

	return {
		evaluationState,
		hasStarted: evaluationState?.hasStarted ?? false,
		currentStep: evaluationState?.currentStep ?? 0,
		isLoading,
		error,
		fetchEvaluationState,
		startEvaluation: handleStartEvaluation,
	};
};
